import { POS_REPORT_BASE } from '@iws/backoffice-api'
import { HttpStateData, Model } from '@/clients/http'

import type { ListQuery, ListResult } from '@/http/models/Resource'

type ErrorBody = { message?: string }

const get = <T,>(key: string, url: string, query?: ListQuery): Promise<HttpStateData<T, ErrorBody>> =>
  Model.createHttpRequest<T, ErrorBody>({
    correlationKey: key,
    payload: { method: 'get', url: Model.createEndpoint({ url, query }), useBearerToken: true },
  })

export interface TerminalShift {
  _id: string
  terminal_id: string
  staff_id: string
  staff?: { name: string } | null
  opening_balance: number
  closing_balance: number | null
  total_cash_in: number
  total_cash_out: number
  expected_balance: number | null
  discrepancy: number | null
  opened_at: string
  closed_at: string | null
  /** Set by the device (`YYYY-MMDD-T<n>-S<k>`); older shifts have none. */
  shift_no?: string | null
}

export interface CashInOut {
  _id: string
  terminal_id: string
  shift_id: string
  staff?: { name: string } | null
  type: 'cash_in' | 'cash_out'
  amount: number
  remark?: string
  created_at: string
}

/** Server-built shift totals (`ShiftReport`, POS.md §15.9 rule 18). */
export interface ShiftReport {
  _id: string
  shift_id: string
  terminal_id: string
  branch_id: string | null
  staff_id: string | null
  opened_at: string
  closed_at: string | null
  opening_balance: number
  sales: { cash: number; card: number; bank: number; qr: number; credit: number }
  refunds: number
  cash_refunds: number
  repayments: number
  cash_repayments: number
  cash_in: number
  cash_out: number
  order_count: number
  expected_cash: number
  closing_balance: number | null
  discrepancy: number | null
}

/** A credit repayment: a `PaymentLog` of type `repayment` (no order). */
export interface CreditRepayment {
  _id: string
  receipt_no?: string | null
  trx_id: string
  payment_method?: string
  amount_received: number
  amount_returned: number
  status: string
  created_at: string
  customer?: { name: string; phone?: string } | null
  terminal?: { name: string; branch?: { name: string } | null } | null
}

export interface DailyProfitLoss {
  _id: string
  branch_id: string
  date: string
  sales_revenue: number
  purchase_cost: number
}

export interface VariationDailyProfitLoss extends DailyProfitLoss {
  product_variation_id: string
}

/** Read-only POS history and reports (checklist C23–C26). Writes happen on the till and arrive through sync. */
export class PosReportModel extends Model {
  public static readonly getShifts = (query: ListQuery) =>
    get<ListResult<TerminalShift>>('GET_TERMINAL_SHIFTS', '/api/backoffice/terminal-shift/list', query)

  public static readonly getCashInOuts = (query: ListQuery) =>
    get<ListResult<CashInOut>>('GET_TERMINAL_CASH', '/api/backoffice/terminal-cash', query)

  public static readonly getShiftReports = (query: ListQuery) =>
    get<ListResult<ShiftReport>>('GET_POS_SHIFT_REPORTS', '/api/backoffice/pos-report/shifts', query)

  public static readonly getCreditRepayments = (query: ListQuery) =>
    get<ListResult<CreditRepayment>>('GET_POS_CREDIT_REPAY', '/api/backoffice/pos-report/credit-repay', query)

  public static readonly getReport = (path: string, query: ListQuery) =>
    get<{ result: Record<string, unknown>[]; count?: number }>(`GET_POS_REPORT_${path}`, `${POS_REPORT_BASE}/${path}`, query)

  public static readonly getProfitLoss = (query: ListQuery) =>
    get<{ result: DailyProfitLoss[] }>('GET_PROFIT_LOSS', '/api/backoffice/profit-loss/list', query)

  public static readonly getProfitLossByProduct = (query: ListQuery) =>
    get<{ result: VariationDailyProfitLoss[] }>('GET_PROFIT_LOSS_BY_PRODUCT', '/api/backoffice/profit-loss/list-by-product', query)
}
